/**
 * Provider 适配器注册表（T03）。
 *
 * 职责：
 * - 按 `ProviderConfig.kind` 实例化适配器；
 * - 支持运行时注册 / 覆盖自定义适配器（`kind: 'custom'` 或覆盖内置 kind）；
 * - 对未注册的 kind 给出明确错误，而不是静默降级。
 *
 * 设计约定：注册表只持有「工厂函数」，不持有配置与密钥，
 * 因此同一个注册表可服务任意多个 ProviderConfig 实例。
 */

import { createOpenAIAdapter } from './openai';
import { createResponsesAdapter } from './responses';
import type {
  ProviderAdapter,
  ProviderAdapterFactory,
  ProviderConfig,
  ProviderDeps,
  ProviderKind,
  ResolvedProviderConfig,
} from './types';

export const BUILTIN_PROVIDER_KINDS: readonly ProviderKind[] = [
  'openai',
  'anthropic',
  'gemini',
  'custom',
] as const;

/**
 * 注册表 API 接受的 kind 参数：任意字符串（支持自定义供应商），同时保留内置 kind 的字面量补全。
 *
 * 注意：不能写成 `ProviderKind | string`——该联合会被 `string` 完全吸收、等价于 `string`，
 * 属于冗余类型分量（no-redundant-type-constituents 会报错）。这里用
 * `ProviderKind | (string & {})`：`string & {}` 让联合类型保持可辨识，
 * 既保留字面量补全，又仍接受任意字符串。
 */
export type ProviderKindInput = ProviderKind | (string & {});

/** 未注册 kind 时抛出；上层可捕获后提示用户配置。 */
export class ProviderNotRegisteredError extends Error {
  readonly kind: string;

  constructor(kind: string) {
    super(`没有为 provider kind "${kind}" 注册适配器`);
    this.name = 'ProviderNotRegisteredError';
    this.kind = kind;
  }
}

export class ProviderRegistry {
  private readonly factories = new Map<string, ProviderAdapterFactory>();

  constructor(options?: { withDefaults?: boolean }) {
    if (options?.withDefaults !== false) {
      this.registerDefaults();
    }
  }

  /** 注册内置适配器。T03 只实现 OpenAI 兼容；Anthropic / Gemini 由 T15 注册。 */
  registerDefaults(): this {
    const createCompatible: ProviderAdapterFactory = (config, deps) => config.protocol === 'responses'
      ? createResponsesAdapter(config, deps)
      : createOpenAIAdapter(config, deps);
    this.register('openai', createCompatible);
    // 'custom' 默认走 OpenAI 兼容协议（绝大多数自建网关都是 OpenAI 兼容）。
    this.register('custom', createCompatible);
    return this;
  }

  /** 注册（或覆盖）某个 kind 的适配器工厂。 */
  register(kind: ProviderKindInput, factory: ProviderAdapterFactory): this {
    if (typeof factory !== 'function') {
      throw new TypeError(`provider 工厂必须是函数，收到 ${typeof factory}`);
    }
    this.factories.set(kind, factory);
    return this;
  }

  /** 注销某个 kind。 */
  unregister(kind: ProviderKindInput): boolean {
    return this.factories.delete(kind);
  }

  has(kind: ProviderKindInput): boolean {
    return this.factories.has(kind);
  }

  /** 已注册的 kind 列表（便于设置页展示与排错）。 */
  kinds(): string[] {
    return [...this.factories.keys()];
  }

  /** 按配置实例化适配器。 */
  create(config: ResolvedProviderConfig, deps?: ProviderDeps): ProviderAdapter {
    const factory = this.factories.get(config.kind);
    if (!factory) {
      throw new ProviderNotRegisteredError(config.kind);
    }
    return factory(config, deps);
  }

  /** 批量实例化，跳过 `enabled === false` 的配置。 */
  createEnabled(configs: readonly ResolvedProviderConfig[], deps?: ProviderDeps): ProviderAdapter[] {
    return configs.filter((config) => config.enabled).map((config) => this.create(config, deps));
  }
}

/** 进程内默认注册表（内置 kind 已注册）。 */
export const defaultProviderRegistry = new ProviderRegistry();

/** 便捷函数：用默认注册表实例化。 */
export function createProviderAdapter(
  config: ResolvedProviderConfig,
  deps?: ProviderDeps,
): ProviderAdapter {
  return defaultProviderRegistry.create(config, deps);
}

/**
 * 归一化外部配置：补齐必填字段并保证 `enabled` 为布尔值。
 * 用于从设置页 / 存储层读取到的宽松对象。
 */
export function normalizeProviderConfig(
  config: ProviderConfig & Partial<Pick<ResolvedProviderConfig, 'apiKey' | 'headers'>>,
): ResolvedProviderConfig {
  return {
    ...config,
    name: config.name || config.kind,
    baseUrl: config.baseUrl,
    apiKeyRef: config.apiKeyRef,
    enabled: config.enabled ?? true,
  };
}
